<template>
  <div class="training-week">
    <!-- Week Navigation -->
    <div class="week-nav">
      <button class="nav-btn" @click="changeWeek(-1)"><i class="bi bi-chevron-left"></i></button>
      <div class="week-title">
        <h3><i class="bi bi-calendar-week"></i> Training Week</h3>
        <span class="week-range">{{ weekRangeLabel }}</span>
      </div>
      <button class="nav-btn" @click="changeWeek(1)"><i class="bi bi-chevron-right"></i></button>
    </div>

    <!-- Match Day Banner(s) -->
    <div v-for="match in matchBannerList" :key="match.id" class="match-banner">
      <i class="bi bi-trophy-fill"></i>
      <span>{{ match.gameType }}: <strong>{{ match.opposition }}</strong></span>
      <span class="match-date">{{ formatMatchDate(match.matchDate) }}</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border spinner-border-sm" role="status"></div>
      <span>Loading training week...</span>
    </div>

    <!-- Timeline -->
    <TrainingWeekTimeline v-else :weekDays="computedWeekDays" @openSession="openSession" />

    <!-- Log Session Button (for players) -->
    <div v-if="isPlayer" class="log-session-row">
      <button class="log-session-btn" @click="showLogModal = true">
        <i class="bi bi-plus-lg"></i> Log Session
      </button>
    </div>

    <!-- Stats -->
    <TrainingWeekStats :weekSessions="allSessions" :seasonData="seasonData" />

    <!-- Session Detail Modal -->
    <SessionDetailModal :show="showSessionModal" :session="selectedSession" :drills="selectedSessionDrills"
      :userData="userData" :selectedUserData="selectedUserData" @close="showSessionModal = false"
      @reportSaved="onReportSaved" @attendanceUpdated="loadWeek" />

    <!-- Log Own Session Modal -->
    <div v-if="showLogModal" class="modal-overlay" @click.self="showLogModal = false">
      <div class="modal-dialog log-modal" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>Log a Session</h3>
            <button @click="showLogModal = false" class="close-button">&times;</button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="submitOwnSession">
              <div class="form-group">
                <label>Session Type</label>
                <select v-model="logForm.training_session_type" class="form-input" required>
                  <option value="Own Training">Own Training</option>
                  <option value="S&C">S&C</option>
                </select>
              </div>
              <div class="form-group">
                <label>Date</label>
                <input type="date" v-model="logForm.matchDate" class="form-input" required />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Start Time</label>
                  <input type="time" v-model="logForm.startTime" class="form-input" />
                </div>
                <div class="form-group">
                  <label>End Time</label>
                  <input type="time" v-model="logForm.endTime" class="form-input" />
                </div>
              </div>
              <div class="form-group">
                <label>Notes (optional)</label>
                <textarea v-model="logForm.notes" class="form-input" rows="2"
                  placeholder="What did you work on?"></textarea>
              </div>
              <button type="submit" class="submit-btn" :disabled="savingLog">
                <i class="bi bi-check-lg"></i> {{ savingLog ? 'Saving...' : 'Log Session' }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { TrainingWeek } from "@/services/training-week-service.js";
import TrainingWeekTimeline from "./TrainingWeekTimeline.vue";
import TrainingWeekStats from "./TrainingWeekStats.vue";
import SessionDetailModal from "./SessionDetailModal.vue";

export default {
  name: "TrainingWeekComponent",
  components: { TrainingWeekTimeline, TrainingWeekStats, SessionDetailModal },
  props: {
    selectedUserData: { type: Object, default: () => ({}) },
    userData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      weekOffset: 0,
      weekStartDate: null,
      weekEndDate: null,
      allSessions: [],
      matchDay: [],
      seasonData: { byType: [], weeklyLoad: [] },
      showSessionModal: false,
      selectedSession: {},
      selectedSessionDrills: [],
      showLogModal: false,
      savingLog: false,
      logForm: {
        training_session_type: "Own Training",
        matchDate: moment().format("YYYY-MM-DD"),
        startTime: "",
        endTime: "",
        notes: "",
      },
    };
  },
  computed: {
    teamId() {
      return this.selectedUserData.teamId || this.userData.teamId;
    },
    userId() {
      return this.selectedUserData.id || this.userData.id;
    },
    isPlayer() {
      return this.userData.userType !== "Admin" && this.userData.userType !== "Coach" && this.userData.userType !== "HeadCoach";
    },
    matchDays() {
      // Collect all match dates from backend response
      const matches = Array.isArray(this.matchDay) ? this.matchDay : (this.matchDay ? [this.matchDay] : []);
      // Also include non-training sessions from allSessions that may not be in matchDay
      const matchDates = new Set(matches.map(m => moment(m.matchDate).format("YYYY-MM-DD")));
      this.allSessions
        .filter(s => s.gameType !== "Training")
        .forEach(s => matchDates.add(moment(s.matchDate).format("YYYY-MM-DD")));
      return matchDates;
    },
    nextMatch() {
      const matches = Array.isArray(this.matchDay) ? this.matchDay : (this.matchDay ? [this.matchDay] : []);
      if (matches.length > 0) return matches[0];
      // Fallback to first non-training session
      const upcoming = this.allSessions
        .filter(s => s.gameType !== "Training")
        .sort((a, b) => a.matchDate.localeCompare(b.matchDate));
      return upcoming.length > 0 ? upcoming[0] : null;
    },
    matchBannerList() {
      const matches = Array.isArray(this.matchDay) ? this.matchDay : (this.matchDay ? [this.matchDay] : []);
      // Also include non-training sessions not already covered
      const ids = new Set(matches.map(m => m.id));
      const extra = this.allSessions.filter(s => s.gameType !== "Training" && !ids.has(s.id));
      return [...matches, ...extra].sort((a, b) => a.matchDate.localeCompare(b.matchDate));
    },
    weekRangeLabel() {
      if (!this.weekStartDate) return "";
      return `${moment(this.weekStartDate).format("D MMM")} - ${moment(this.weekEndDate).format("D MMM YYYY")}`;
    },
    computedWeekDays() {
      const days = [];
      const start = moment(this.weekStartDate);
      const today = moment().format("YYYY-MM-DD");
      const matchDateSet = this.matchDays;

      // Build sorted array of match dates for relative labelling
      const sortedMatchDates = [...matchDateSet].sort();

      for (let i = 0; i < 7; i++) {
        const date = start.clone().add(i, "days");
        const dateStr = date.format("YYYY-MM-DD");
        const sessions = this.allSessions.filter(
          (s) => moment(s.matchDate).format("YYYY-MM-DD") === dateStr
        );

        let mdLabel = `Day ${i + 1}`;
        let isMatchDay = false;

        if (matchDateSet.has(dateStr)) {
          // This day IS a match day
          mdLabel = "MD";
          isMatchDay = true;
        } else if (sortedMatchDates.length > 0) {
          // Find the nearest match day and label relative to it
          let nearestMatch = sortedMatchDates[0];
          let minDist = Math.abs(date.diff(moment(nearestMatch), "days"));
          for (const md of sortedMatchDates) {
            const dist = Math.abs(date.diff(moment(md), "days"));
            if (dist < minDist) {
              minDist = dist;
              nearestMatch = md;
            }
          }
          const diff = date.diff(moment(nearestMatch), "days");
          mdLabel = diff > 0 ? `MD+${diff}` : `MD${diff}`;
        }

        days.push({
          date: dateStr,
          dayName: date.format("ddd"),
          dateFormatted: date.format("D MMM"),
          isToday: dateStr === today,
          isMatchDay,
          mdLabel,
          sessions,
        });
      }
      return days;
    },
  },
  watch: {
    selectedUserData: {
      handler() {
        this.loadWeek();
        this.loadSeasonStats();
      },
      deep: true,
    },
  },
  created() {
    this.setWeekDates(0);
    this.loadWeek();
    this.loadSeasonStats();
  },
  methods: {
    setWeekDates(offset) {
      this.weekOffset = offset;
      this.weekStartDate = moment().add(offset, "weeks").startOf("isoWeek").format("YYYY-MM-DD");
      this.weekEndDate = moment().add(offset, "weeks").endOf("isoWeek").format("YYYY-MM-DD");
    },
    changeWeek(dir) {
      this.setWeekDates(this.weekOffset + dir);
      this.loadWeek();
    },
    async loadWeek() {
      if (!this.teamId || !this.userId) return;
      this.loading = true;

      try {
        const [weekRes, matchRes] = await Promise.all([
          TrainingWeek.getWeekData({
            teamId: this.teamId,
            userId: this.userId,
            weekStartDate: this.weekStartDate,
            weekEndDate: this.weekEndDate,
          }),
          TrainingWeek.getMatchDay({
            teamId: this.teamId,
            weekStartDate: this.weekStartDate,
            weekEndDate: this.weekEndDate,
          }),
        ]);

        this.allSessions = Array.isArray(weekRes.data) ? weekRes.data : [];
        // Backend now returns an array of matches (or legacy single object)
        this.matchDay = matchRes.data || [];
      } catch (err) {
        console.error("Error loading training week:", err);
        this.allSessions = [];
        this.matchDay = [];
      } finally {
        this.loading = false;
      }
    },
    async loadSeasonStats() {
      if (!this.teamId || !this.userId) return;
      const seasonStart = localStorage.getItem("currentPlayingseasonStartDate") || moment().subtract(6, "months").format("YYYY-MM-DD");
      const seasonEnd = localStorage.getItem("currentPlayingseasonEndDate") || moment().add(6, "months").format("YYYY-MM-DD");

      try {
        const res = await TrainingWeek.getSeasonStats({
          teamId: this.teamId,
          userId: this.userId,
          season_start_date: seasonStart,
          season_end_date: seasonEnd,
        });
        this.seasonData = res.data || { byType: [], weeklyLoad: [] };
      } catch (err) {
        console.error("Error loading season stats:", err);
      }
    },
    async openSession(session) {
      this.selectedSession = { ...session };
      this.selectedSessionDrills = [];
      this.showSessionModal = true;

      if (session.drill_count > 0) {
        try {
          const res = await TrainingWeek.getSessionDrills(session.id);
          this.selectedSessionDrills = res.data || [];
        } catch (err) {
          console.error("Error loading drills:", err);
        }
      }
    },
    onReportSaved() {
      this.showSessionModal = false;
      this.loadWeek();
      this.loadSeasonStats();
    },
    formatMatchDate(d) {
      return d ? moment(d).format("ddd D MMM") : "";
    },
    async submitOwnSession() {
      this.savingLog = true;
      const kickOffTime = this.logForm.startTime
        ? `${this.logForm.matchDate} ${this.logForm.startTime}:00`
        : `${this.logForm.matchDate} 00:00:00`;
      const endTime = this.logForm.endTime
        ? `${this.logForm.matchDate} ${this.logForm.endTime}:00`
        : null;

      try {
        await TrainingWeek.savePlayerOwnSession({
          matchDate: this.logForm.matchDate,
          kickOffTime,
          endTime,
          training_session_type: this.logForm.training_session_type,
          notes: this.logForm.notes,
          teamId: this.teamId,
          teamName: this.selectedUserData.teamName || "",
          userId: this.userId,
        });
        this.showLogModal = false;
        this.logForm = {
          training_session_type: "Own Training",
          matchDate: moment().format("YYYY-MM-DD"),
          startTime: "",
          endTime: "",
          notes: "",
        };
        this.loadWeek();
      } catch (err) {
        console.error("Error saving session:", err);
      } finally {
        this.savingLog = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.training-week {
  width: 100%;
  border-top: #c3cdd5 dashed;
  padding-top: 1rem;
}

.week-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #b5bec6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #3b82f6;
    color: #3b82f6;
  }
}

.week-title {
  text-align: center;

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    i {
      color: #3b82f6;
    }
  }

  .week-range {
    font-size: 0.78rem;
    color: #6b7280;
  }
}

.match-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fef2f2, #fff1f2);
  border: 1px solid #fecaca;
  border-radius: 10px;
  margin-bottom: 12px;
  font-size: 0.85rem;
  color: #991b1b;

  i {
    color: #ef4444;
    font-size: 1rem;
  }

  strong {
    font-weight: 700;
  }

  .match-date {
    margin-left: auto;
    font-size: 0.78rem;
    color: #dc2626;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px;
  color: #6b7280;
  font-size: 0.9rem;
}

.log-session-row {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.log-session-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border-radius: 20px;
  border: 2px dashed #d1d5db;
  background: #fff;
  color: #6b7280;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #22c55e;
    color: #22c55e;
    background: #f0fdf4;
  }
}

/* Log Session Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.log-modal {
  background: #fff;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  overflow: hidden;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 600;
  }
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #9ca3af;

  &:hover {
    color: #1f2937;
  }
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 14px;

  label {
    display: block;
    font-size: 0.82rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
  }
}

.form-row {
  display: flex;
  gap: 12px;

  .form-group {
    flex: 1;
  }
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
  }
}

select.form-input {
  cursor: pointer;
}

textarea.form-input {
  resize: vertical;
}

.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
